import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Outlet, useParams } from "react-router";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { getSession, type SessionDetailDto } from "~/lib/api";
import { cn } from "~/lib/utils";
import NotFoundRoute from "~/routes/not-found";
import { SessionProvider } from "~/session/session-context";

type State =
  | { status: "loading" }
  | { status: "ready"; session: SessionDetailDto }
  | { status: "not-found" }
  | { status: "error"; message: string };

const SHIFT_LABEL: Record<SessionDetailDto["shiftType"], string> = {
  day: "Day shift",
  night: "Night shift",
};

// E7.3 — once the session locks, every nested route renders the same muted
// banner so the doctor lands on any patient or list view and immediately reads
// "this is finished, you can't edit". Kept as a constant rather than inline
// for the test ids in `session-layout.spec.tsx`.
const LOCK_BANNER_COPY = "This handover is complete. The record is locked.";

/**
 * Layout route for `/session/:id/*`. Fetches the session once on mount (and when
 * the id changes), provides it to children via context, and renders the session
 * shell header with the ward name. A 404 from the API renders the shared
 * not-found page — the API's access check is authoritative, so the frontend
 * doesn't run its own membership check. Session loading lives here, not in the
 * global AppShell, because it's specific to session routes.
 */
export default function SessionLayout(): JSX.Element {
  const { id } = useParams();
  const [state, setState] = useState<State>({ status: "loading" });
  // Retry bumps this so the effect re-runs through its own cancellation signal,
  // rather than firing a detached fetch that can't be cancelled on navigation.
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(
    async (signal: { cancelled: boolean }): Promise<void> => {
      if (!id) {
        setState({ status: "not-found" });
        return;
      }
      setState({ status: "loading" });
      try {
        const result = await getSession({ path: { id } });
        if (signal.cancelled) return;
        if (result.response.status === 404) {
          setState({ status: "not-found" });
          return;
        }
        if (result.error || !result.data) {
          setState({
            status: "error",
            message: `Couldn't load the session (API responded with ${result.response.status}).`,
          });
          return;
        }
        setState({ status: "ready", session: result.data });
      } catch (err) {
        if (signal.cancelled) return;
        setState({
          status: "error",
          message: err instanceof Error ? err.message : "Unknown error",
        });
      }
    },
    [id],
  );

  useEffect(() => {
    const signal = { cancelled: false };
    void load(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [load, reloadKey]);

  // E7.3 optimistic lock: the detail screen calls this after a successful
  // acknowledge whose response carries `sessionLocked: true`, so the banner
  // and the hidden edit affordances appear in the same frame without a second
  // GET /sessions/:id. A no-op while we're not in `ready` — there's no
  // session to flip yet.
  const markSessionLocked = useCallback((): void => {
    setState((prev) =>
      prev.status === "ready" && prev.session.status !== "locked"
        ? { status: "ready", session: { ...prev.session, status: "locked" } }
        : prev,
    );
  }, []);

  if (state.status === "loading") {
    return (
      <div className="flex items-center justify-center py-16" aria-live="polite">
        <Loader2
          className="h-6 w-6 animate-spin text-muted-foreground"
          aria-label="Loading session"
        />
      </div>
    );
  }

  if (state.status === "not-found") {
    return <NotFoundRoute />;
  }

  if (state.status === "error") {
    return (
      <Alert variant="destructive">
        <AlertTitle>Something went wrong</AlertTitle>
        <AlertDescription className="flex flex-col items-start gap-3">
          <span>{state.message}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setReloadKey((k) => k + 1);
            }}
          >
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const { session } = state;
  const isLocked = session.status === "locked";
  return (
    <SessionProvider session={session} markSessionLocked={markSessionLocked}>
      <div className="space-y-6">
        {isLocked ? (
          // Sticky so it stays in view on long patient lists / detail pages —
          // the spec calls it out as the persistent reminder that no edits will
          // land. role="status" + aria-live="polite" announces the lock to AT
          // when the optimistic update flips it mid-page (after Acknowledge).
          <div
            role="status"
            aria-live="polite"
            data-testid="session-lock-banner"
            className="sticky top-0 z-20 -mx-4 border-b border-border bg-muted px-4 py-2 text-sm text-muted-foreground sm:-mx-6 sm:px-6"
          >
            {LOCK_BANNER_COPY}
          </div>
        ) : null}
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-4">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">{session.wardName}</h1>
            <p className="text-sm text-muted-foreground">
              {SHIFT_LABEL[session.shiftType]} ·{" "}
              <span className="font-mono">{session.wardShortCode}</span>
            </p>
          </div>
          <span
            className={cn(
              "rounded-full px-2.5 py-0.5 text-xs font-medium capitalize",
              // Locked = "complete" semantically; the green tint matches the
              // home recent-sessions Complete pill and the patient-card
              // acknowledged check so the colour is consistent across screens.
              isLocked ? "bg-priority-low/15 text-priority-low" : "bg-muted text-muted-foreground",
            )}
          >
            {isLocked ? "Complete" : session.status}
          </span>
        </header>
        <Outlet />
      </div>
    </SessionProvider>
  );
}
